import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import type { ElementRef, OnInit } from '@angular/core';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import {
  ActionHelper,
  OnyxButtonComponent,
  OnyxIconButtonComponent,
  OnyxModalComponent,
} from '@onyx/angular';
import { cloneDeep } from 'lodash';
import { concatMap, forkJoin, from, map, of, Subject } from 'rxjs';
import { StorageService } from '../../../../../../common/services/storage.service';
import type { DriverReportModalFileData } from '../driver-report-modal.component';

export interface DriverReportModalCarouselData {
  data: DriverReportModalFileData[];
  selected: DriverReportModalFileData;
}

interface SelectedImageData {
  uuid: string | null;
  image: File | null;
  thumbnail: string | null;
  title: string;
  note?: string;
}

@Component({
  selector: 'app-driver-report-modal-carousel',
  imports: [
    OnyxModalComponent,
    OnyxIconButtonComponent,
    TranslatePipe,
    OnyxButtonComponent,
  ],
  templateUrl: './driver-report-modal-carousel.component.html',
  styleUrl: './driver-report-modal-carousel.component.scss',
})
export class DriverReportModalCarouselComponent implements OnInit {
  protected readonly data = inject<DriverReportModalCarouselData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef<void>);
  protected readonly actionHelper = inject(ActionHelper);
  private readonly storageService = inject(StorageService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly ActionHelper = ActionHelper;

  private readonly thumbnailElementRefs =
    viewChildren<ElementRef>('thumbnailElement');

  protected items = signal<SelectedImageData[]>([]);
  protected selectedItemData = signal<SelectedImageData | null>(null);
  protected close$ = new Subject<void>();

  protected selectedIndex = computed(() =>
    this.items().findIndex(
      (item) => item.uuid === this.selectedItemData()?.uuid,
    ),
  );

  protected allFiles = computed(() =>
    this.items().flatMap((item) => item.image),
  );

  public ngOnInit(): void {
    const { data, selected } = this.data;

    this.selectedItemData.set({
      uuid: selected.files[0],
      image: selected.images[0],
      thumbnail: URL.createObjectURL(selected.images[0]),
      title: selected.title,
      note: selected.note,
    });

    const items: SelectedImageData[] = data.flatMap((group) =>
      group.files.map((uuid) => ({
        uuid,
        image: null,
        thumbnail: null,
        title: group.title,
        note: group.note,
      })),
    );
    this.items.set(items);
    setTimeout(() => this.scrollToElement(this.selectedIndex(), 'center'));

    let index = 0;
    from(data)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        concatMap((group) =>
          group.files.length
            ? forkJoin(
                group.files.map((fileId) =>
                  this.storageService.getFile(fileId),
                ),
              ).pipe(
                map((files) =>
                  files.map((file, i) => ({
                    uuid: group.files[i],
                    image: file,
                    thumbnail: URL.createObjectURL(file),
                    title: group.title,
                    note: group.note,
                  })),
                ),
              )
            : of([]),
        ),
      )
      .subscribe((addedItems) => {
        this.items.update((current) => {
          const updated = cloneDeep(current);
          for (let i = 0; i < addedItems.length; ++i) {
            updated[index + i] = addedItems[i];
          }

          index += addedItems.length;
          return updated;
        });
      });
  }

  protected previous(): void {
    const index = this.selectedIndex();
    if (index <= 0) return;

    this.selectedItemData.set(this.items()[index - 1]);
    setTimeout(() => this.scrollToElement(index - 1, 'start'));
  }

  protected next(): void {
    const index = this.selectedIndex();
    const allItems = this.items();
    if (index >= allItems.length - 1) return;

    this.selectedItemData.set(allItems[index + 1]);
    setTimeout(() => this.scrollToElement(index + 1, 'end'));
  }

  protected selectImage(image: File | null): void {
    if (!image) return;

    const selected = this.items().find((item) => item.image === image);
    if (!selected) return;

    this.selectedItemData.set(selected);
  }

  protected downloadFiles(): void {
    if (!this.allFiles().length) return;

    for (const file of this.allFiles()) {
      if (file == null) continue;
      this.actionHelper.downloadFile(file);
    }
  }

  private scrollToElement(
    index: number,
    block: 'start' | 'end' | 'center',
  ): void {
    const elements = this.thumbnailElementRefs();
    const target = elements[index];
    if (!target) return;

    target.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block,
      inline: 'center',
    });
  }
}
